/**
 * propertyValidation —— 合作内容「属性字段」的校验语义层（纯函数，无 React）。
 *
 * 【与折叠面板同源】本文件的规则是 `#features/utils.getOptionsRule` + `getRequiredRule`
 * 的**镜像实现**（折叠模式用 form rules，表格模式没有 Form.Item 承载，只能自己判）。
 * 注释里逐条标注了与 `getOptionsRule` 的对应关系 —— 改这里前必须两侧一起看，
 * 否则表格模式与折叠模式会悄悄分叉。
 *
 * 【⚠️ 为什么不放进 propertyFieldConfig.ts】那个文件已 330 行，职责是「控件配置元数据映射」；
 * 塞进来会造出 380 行双职责文件，并扩大折叠模式的回归面。
 *
 * 【⚠️ 为什么 `isEmptyFieldValue` 落在这里而不是 propertyColumns.tsx】
 * propertyColumns 的列定义要 import 本文件的 `validatePropField`；若 `isEmptyFieldValue`
 * 反向放在 propertyColumns，就构成**循环 import**。它是「空值」这一校验语义的判据，
 * 且被 propertyColumns / needAiColumn / useRowMutations 三处共用，放这里依赖方向唯一。
 *
 * 【与引擎 `isEmpty` 的差异（有意）】引擎 `fieldRules.isEmpty` 只判
 * undefined/null/空串（因为 `required` 遇到 `[]` 不报错）；本文件的 `isEmptyFieldValue`
 * **额外把空数组也算空** —— 属性值可能是多选数组，`[]` 在业务上就是「没填」。
 * 两者语义不同、各自服务自己的层，**不要互相替换**。
 */
import { getSchemaItemFieldConfig, isSchemaItemRequired } from './propertyFieldConfig';
import { cooperationValueToFormValue, getPropertyCode } from './propertyPipe';

/** 字段值是否为空（业务口径：**含空数组**。与引擎 fieldRules.isEmpty 的差异见文件头） */
export const isEmptyFieldValue = (value: any) =>
  value === undefined || value === null || value === '' || (Array.isArray(value) && !value.length);

/**
 * 属性列必填判据（PropField 红框、表头星号与表格整体校验共用，避免 UI / 校验两份逻辑漂移）。
 * ⚠️ 必须与折叠面板模式同一来源（isSchemaItemRequired → schema 的 required_mode /
 * schemaItem.required），不能用 rules.some(r => r.required)——validator 形态的必填规则
 * （如 text_with_upload）没有 required 字面量，会漏判必填。
 */
export const getPropRequired = (schemaItem: any, formMap: Record<string, any>): boolean =>
  isSchemaItemRequired(schemaItem, formMap);

/**
 * 属性值是否落在「当前行上下文下的合法选项集」内。
 *
 * 对齐折叠面板 `utils.getOptionsRule` 的「选项不匹配」判定，语义逐条一致：
 *  - **该控件没有选项集（`options` 为 `undefined`）→ 恒视为匹配**。input / text / numeric /
 *    date 这些控件本来就没有「选项」概念，若照空数组判，**任何非空值都会被判成「选项不匹配」**
 *    ——它们一填就报错（实测缺陷，2026-09-11 修）；
 *  - **空值 → 视为匹配**（不匹配与否由必填规则负责，避免同一格被两条规则重复报错）；
 *  - **多选值 → 需全部命中**；
 *  - 选项集必须按**当前行**求值（getLogicOptions 按 standardCode 上级 / use_condition 动态过滤）。
 *    这正是该校验的意义：级联父字段变化后，子字段旧值可能已失效。
 *
 * ⚠️ 务必区分「`options` 为 `undefined`」与「`options` 为空数组」：后者是枚举控件在当前行
 * 上下文下**求值为空**（如级联父字段尚未选择），仍应把旧值判为不匹配 —— 那正是本条校验存在的意义。
 */
export const isPropValueMatched = (value: any, options: { value: string }[] | undefined): boolean => {
  if (!options) return true;
  if (isEmptyFieldValue(value)) return true;
  const vals = Array.isArray(value) ? value : [value];
  return vals.every((v) => options.some((o) => o.value === v));
};

/**
 * 取当前行上下文下的合法选项（级联求值结果，随行不同而不同）。
 *
 * ⚠️ **返回 `undefined` 表示该控件没有「选项集」概念**（input / text / numeric / date）。
 * 这里**不能**再兜底成 `|| []` —— 那会把「没有选项」与「选项为空」混为一谈，
 * 让 `isPropValueMatched` 无从区分，从而把所有非空值判成不匹配（即上面那个缺陷）。
 */
const getPropOptions = (schemaItem: any, formMap: Record<string, any>, structuredVersion?: number): { value: string }[] | undefined =>
  getSchemaItemFieldConfig(schemaItem, formMap, structuredVersion).elementProps?.options;

/**
 * 属性列校验：返回错误信息（无错返回 undefined）。
 *
 * 属性值存放在 row.property_values[code] 中，引擎无从取值，故经 CustomColumnMeta.validate
 * 提供给引擎；使属性列（主平台单、主平台账号、合作项目等）真正参与校验与滚动定位。
 *
 * 两条规则与折叠面板对齐（`getOptionsRule` + `getRequiredRule`）：
 *  ① **选项不匹配** —— 级联父字段（如「主平台官单/私单」）变化后，子字段（如「主平台报价项目」）
 *     的旧值可能已不在当前合法选项集内 → 提示用户重选。**此条曾在表格模式缺失**。
 *     ⚠️ **仅对「有选项集」的控件（dropdown_list / dropdown_radio）生效**；input / text /
 *     numeric / date 没有选项概念，由 `isPropValueMatched` 的 `!options` 分支直接放行，
 *     不会误报「选项不匹配」。
 *  ② **必填**。
 * 二者互斥（一条判非空、一条判空），不会同时命中，故返回顺序不影响结果。
 */
export const validatePropField = (schemaItem: any, row: Record<string, any>, structuredVersion?: number): string | undefined => {
  const formMap = cooperationValueToFormValue(row.property_values);
  const code = getPropertyCode(schemaItem);
  const value = formMap[code];

  if (!isPropValueMatched(value, getPropOptions(schemaItem, formMap, structuredVersion))) return '选项不匹配';

  if (!getPropRequired(schemaItem, formMap)) return undefined;
  return isEmptyFieldValue(value)
    ? `${schemaItem.label ?? schemaItem.property_name ?? '此项'}必填`
    : undefined;
};
